import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CreateAppointmentModal from './CreateAppointment';
import '../Appointments/Appointments.css';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import { FaThList } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SetHolidayModal from './SetHolidayModal';
import BlockDateModal from './BlockDateModal';
import BlockDateAndTimeModal from './BlockDateAndTimeModal';
import SetPatientLimitModal from './SetPatientModal';
import AccessControl from '../AccessControl';

const localizer = momentLocalizer(moment);

const Appointments = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [appointments, setAppointments] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [holidays, setHolidays] = useState(() => {
        const savedHolidays = localStorage.getItem('holidays');
        return savedHolidays ? JSON.parse(savedHolidays) : [];
    });
    const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
    const [blockedDates, setBlockedDates] = useState([]);
    const [blockedDatesAndTime, setBlockedDatesAndTime] = useState([]);
    const [patientLimits, setPatientLimits] = useState([]);
    const [isBlockDateModalOpen, setIsBlockDateModalOpen] = useState(false);
    const [isBlockDateAndTimeModalOpen, setIsBlockedDateAndTimeModalOpen] = useState(false);
    const [isPatientLimitModalOpen, setIsPatientLimitModalOpen] = useState(false);
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const fetchAppointments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments`, {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to fetch appointments');
            }
            const data = await response.json();

            // Filter out archived appointments
            const filteredAppointments = data.filter(appointment => !appointment.isArchived);

            const formattedAppointments = filteredAppointments.map(appointment => {
                const startString = `${appointment.date.split('T')[0]}T${appointment.startTime}:00`;
                const endString = `${appointment.date.split('T')[0]}T${appointment.endTime}:00`;

                const startDate = new Date(startString);
                const endDate = new Date(endString);

                return {
                    id: appointment._id,
                    title: appointment.reason,
                    start: startDate,
                    end: endDate,
                    allDay: false,
                };
            });

            setAppointments(formattedAppointments);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data from localStorage on load
    useEffect(() => {
        const fetchData = async () => {
            fetchAppointments();
            try {
                const [holidaysRes, patientLimitsRes, blockedDatesRes, blockedDatesAndTimeRes] = await Promise.all([
                    fetch('https://api.hgcdental.clinic/api/holidays'),
                    fetch('https://api.hgcdental.clinic/api/patientLimits'),
                    fetch('https://api.hgcdental.clinic/api/blockedDates'),
                    fetch('https://api.hgcdental.clinic/api/blockedDatesAndTime'),
                ]);

                const holidays = await holidaysRes.json();
                const patientLimits = await patientLimitsRes.json();
                const blockedDates = await blockedDatesRes.json();
                const blockedDatesAndTime = await blockedDatesAndTimeRes.json();

                setHolidays(holidays.map((h) => h.date));
                setPatientLimits(patientLimits);
                setBlockedDates(blockedDates.map((b) => b.date));
                setBlockedDatesAndTime(blockedDatesAndTime.map((b) => b.date));
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleSelectEvent = (event) => {
        navigate(`/appointment-details/${event.id}`);
    };
    const handleSelectSlot = ({ start, end }) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison

        if (start < today) {
            alert("You cannot select a date before today.");
        } else {
            // Redirect to the create-appointment page
            navigate('/create-appointment');
        }
    };

    const handleNewAppointmentClick = () => {
        setShowForm(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false); // Close the modal
    };

    const handleOpenModal = () => {
        setIsModalOpen(true); // Open the modal
    };

    const upcomingAppointment = appointments.length ? appointments[0] : null;

    // Function to handle adding holidays
    // const handleAddHoliday = () => {
    //     const date = prompt('Enter holiday date (YYYY-MM-DD):');
    //     if (date) {
    //         const updatedHolidays = [...holidays, date];
    //         setHolidays(updatedHolidays);
    //         localStorage.setItem('holidays', JSON.stringify(updatedHolidays)); // Persist to localStorage
    //     }
    // };

    // Function to handle blocking dates
    const handleBlockDate = () => {
        const date = prompt('Enter blocked date (YYYY-MM-DD):');
        if (date) {
            const updatedBlockedDates = [...blockedDates, date];
            setBlockedDates(updatedBlockedDates);
            localStorage.setItem('blockedDates', JSON.stringify(updatedBlockedDates)); // Persist to localStorage
        }
    };

    // Function to handle setting patient limit
    const handleSetPatientLimit = () => {
        const date = prompt('Enter date for patient limit (YYYY-MM-DD):');
        if (date) {
            const limit = prompt('Enter patient limit:');
            if (limit) {
                const limitEvent = {
                    title: `Patient Limit: ${limit}`,
                    start: new Date(date),
                    end: new Date(date),
                    allDay: true,
                    limit: limit, // Store the patient limit in the event
                };
                const updatedPatientLimits = [...patientLimits, limitEvent];
                setPatientLimits(updatedPatientLimits);
                localStorage.setItem('patientLimits', JSON.stringify(updatedPatientLimits)); // Persist to localStorage
            }
        }
    };

    const holidayEvents = holidays.map((holiday) => ({
        title: 'Holiday',
        start: new Date(holiday),
        end: new Date(holiday),
        allDay: true,
    }));

    const blockedEvents = blockedDates.map((date) => ({
        title: 'Blocked Date',
        start: new Date(date),
        end: new Date(date),
        allDay: true,
    }));

    const blockedEventsAndTime = blockedDatesAndTime.map((date) => ({
        title: 'Blocked Date And Time',
        start: new Date(date),
        end: new Date(date),
        allDay: true,
    }));

    const allEvents = [...appointments, ...holidayEvents, ...blockedEvents, ...patientLimits, ...blockedEventsAndTime];

    return (
        <AccessControl>
            <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
                <InternalHeader moduleName="Appointments" toggleSidebar={toggleSidebar} />
                <div className="appointments-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="data-table-container2">
                        <div className="data-table-header">
                            <a href="/create-appointment"><button className='create-button'>New</button></a>
                            {isModalOpen && (
                                <CreateAppointmentModal
                                    onClose={handleCloseModal} // Pass the function to close the modal
                                    fetchAppointments={fetchAppointments}
                                />
                            )}
                            <div class="button-container">
                                <a href="/appointments-table">
                                    <button className="view-toggle-button1"><FaThList /></button>
                                </a>
                            </div>
                        </div>
                        <div className="appointments-layout">
                            <div className="data-table-container-appointments">
                                {loading ? (
                                    <p>Loading appointments...</p>
                                ) : error ? (
                                    <p className="error-message">{error}</p>
                                ) : (
                                    <Calendar
                                        localizer={localizer}
                                        events={allEvents}
                                        startAccessor="start"
                                        endAccessor="end"
                                        style={{ height: 600, margin: '20px', flex: 1 }}
                                        selectable
                                        onSelectEvent={handleSelectEvent}
                                        onSelectSlot={handleSelectSlot}
                                        eventPropGetter={(event) => {
                                            if (event.title === 'Holiday') {
                                                return { style: { backgroundColor: 'yellow' } };
                                            }
                                            if (event.title === 'Blocked Date') {
                                                return { style: { backgroundColor: 'red' } };
                                            }
                                            if (event.title.startsWith('Patient Limit')) {
                                                return { style: { backgroundColor: 'orange', color: 'white' } };
                                            }
                                            return {};
                                        }}
                                    />
                                )}
                                {showForm && (
                                    <CreateAppointmentModal
                                        onClose={() => setShowForm(false)}
                                        onSubmit={fetchAppointments}
                                        setAppointments={setAppointments}
                                    />
                                )}
                            </div>
                            <div className="upcoming-appointment">
                                {upcomingAppointment ? (
                                    <>
                                        <h2>Upcoming Appointment</h2>
                                        <p><b>Patient Name:</b> {upcomingAppointment.title}</p>
                                        <p><b>Reason:</b> {upcomingAppointment.title}</p>
                                        <p><b>Date:</b> {moment(upcomingAppointment.start).format('YYYY-MM-DD')}</p>
                                        <p><b>Start Time:</b> {moment(upcomingAppointment.start).format('HH:mm')}</p>
                                        <p><b>End Time:</b> {moment(upcomingAppointment.end).format('HH:mm')}</p>
                                    </>
                                ) : (
                                    <p>No upcoming appointments.</p>
                                )}
                                {/* Buttons below upcoming appointments */}
                                <div className="appointment-actions">
                                    <button onClick={() => setIsHolidayModalOpen(true)} className="action-button">Add Holiday</button>
                                    <button onClick={() => setIsBlockDateModalOpen(true)} className="action-button">Block Date</button>
                                    <button onClick={() => setIsBlockedDateAndTimeModalOpen(true)} className="action-button">Block Date And Time</button>
                                    <button onClick={() => setIsPatientLimitModalOpen(true)} className="action-button">Set Patient Limit</button>
                                </div>

                                <SetHolidayModal
                                    isOpen={isHolidayModalOpen}
                                    onClose={() => setIsHolidayModalOpen(false)}
                                    holidays={holidays}
                                    setHolidays={setHolidays}
                                />

                                <BlockDateModal
                                    isOpen={isBlockDateModalOpen}
                                    onClose={() => setIsBlockDateModalOpen(false)}
                                    blockedDates={blockedDates}
                                    setBlockedDates={setBlockedDates}
                                />

                                <BlockDateAndTimeModal
                                    isOpen={isBlockDateAndTimeModalOpen}
                                    onClose={() => setIsBlockedDateAndTimeModalOpen(false)}
                                    blockedDatesAndTime={blockedDatesAndTime}
                                    setBlockedDatesAndTime={setBlockedDatesAndTime}
                                />

                                <SetPatientLimitModal
                                    isOpen={isPatientLimitModalOpen}
                                    onClose={() => setIsPatientLimitModalOpen(false)}
                                    patientLimits={patientLimits}
                                    setPatientLimits={setPatientLimits}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};
//why

export default Appointments;